import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, CameraOff, LogOut, ScanLine, ShieldCheck, ShieldX } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api, apiError } from "../api";
import { useAuth } from "../auth";
import { useLang } from "../i18n";

const READER_ID = "lakou-qr-reader";

export default function Checkin() {
    const { user, loading, logout } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const [code, setCode] = useState("");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [camOn, setCamOn] = useState(false);
    const [camError, setCamError] = useState("");
    const scannerRef = useRef(null);

    useEffect(() => {
        if (!loading && (!user || !["staff", "admin"].includes(user.role))) nav("/login", { replace: true });
    }, [user, loading, nav]);

    const stopCamera = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
                scannerRef.current.clear();
            } catch {}
            scannerRef.current = null;
        }
        setCamOn(false);
    };

    useEffect(() => () => { stopCamera(); }, []);

    const submitCode = async (raw) => {
        const value = (raw || "").trim().toUpperCase();
        if (!value || busy) return;
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/checkin", { code: value });
            setResult(data);
        } catch (err) {
            setResult({ result: "error", message: apiError(err, "Check-in failed") });
        } finally {
            setBusy(false);
            setCode("");
        }
    };

    const startCamera = async () => {
        setCamError("");
        setResult(null);
        try {
            const scanner = new Html5Qrcode(READER_ID);
            scannerRef.current = scanner;
            setCamOn(true);
            await scanner.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 240, height: 240 } },
                async (decoded) => {
                    await stopCamera();
                    submitCode(decoded);
                },
                () => {}
            );
        } catch (e) {
            setCamOn(false);
            scannerRef.current = null;
            setCamError("Camera unavailable — check permissions, or type the code below.");
        }
    };

    if (loading || !user) return <div className="pt-40 text-center text-lakou-sand text-sm">Loading…</div>;

    const styles = {
        ok: "border-green-500/40 bg-green-500/10 text-green-400",
        already_used: "border-lakou-gold/40 bg-lakou-gold/10 text-lakou-gold",
        cancelled: "border-red-500/50 bg-red-500/15 text-red-400",
        invalid: "border-lakou-orangelight/40 bg-lakou-orange/10 text-lakou-orangelight",
        error: "border-red-500/40 bg-red-500/10 text-red-400",
    };

    return (
        <div data-testid="checkin-page" className="pt-36 pb-24 min-h-screen">
            <div className="max-w-md mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-lakou-gold">Door staff</div>
                        <h1 className="mt-2 font-display font-extrabold text-3xl text-lakou-cream tracking-tight">{t("ci_title")}</h1>
                    </div>
                    <button onClick={async () => { await stopCamera(); await logout(); nav("/login"); }} data-testid="checkin-logout" aria-label="Log out"
                        className="p-3 rounded-full border border-lakou-gold/25 text-lakou-grey hover:text-lakou-cream transition-colors">
                        <LogOut size={16} />
                    </button>
                </div>

                <div className="mt-8 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/80 p-7 space-y-5">
                    <button
                        type="button"
                        onClick={camOn ? stopCamera : startCamera}
                        data-testid="checkin-camera-toggle"
                        className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 ${
                            camOn ? "border border-lakou-orangelight/50 text-lakou-orangelight" : "bg-lakou-gold text-lakou-ink hover:bg-lakou-goldbright"
                        }`}
                    >
                        {camOn ? <><CameraOff size={15} /> {t("ci_stop")}</> : <><Camera size={15} /> {t("ci_scan")}</>}
                    </button>

                    <div className={camOn ? "" : "hidden"}>
                        <div id={READER_ID} data-testid="qr-reader" className="rounded-2xl overflow-hidden border border-lakou-gold/25 bg-lakou-void [&_video]:!w-full" />
                        <p className="mt-2 text-center text-[11px] text-lakou-sand">Point the camera at the guest's QR code — check-in is automatic.</p>
                    </div>
                    {camError && <p className="text-xs text-lakou-orangelight text-center" data-testid="camera-error">{camError}</p>}

                    <div className="flex items-center gap-4">
                        <span className="flex-1 h-px bg-lakou-gold/15" />
                        <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{t("ci_or")}</span>
                        <span className="flex-1 h-px bg-lakou-gold/15" />
                    </div>

                    <form onSubmit={(e) => { e.preventDefault(); submitCode(code); }} className="space-y-4" data-testid="checkin-form">
                        <input
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="LAKOU-XXXXXXXX"
                            data-testid="checkin-code-input"
                            className="w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-4 font-mono2 text-lg tracking-[0.15em] text-lakou-cream placeholder:text-lakou-sand/50 focus:outline-none focus:border-lakou-gold transition-colors text-center"
                        />
                        <button type="submit" disabled={busy || !code.trim()} data-testid="checkin-submit"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 disabled:opacity-50">
                            <ScanLine size={15} /> {busy ? "…" : t("ci_validate")}
                        </button>
                    </form>
                </div>

                {result && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                        data-testid="checkin-result"
                        className={`mt-6 rounded-3xl border p-6 ${styles[result.result] || styles.error}`}
                    >
                        <div className="flex items-center gap-3 font-display font-bold text-lg">
                            {result.result === "ok" ? <ShieldCheck size={22} /> : <ShieldX size={22} />}
                            {result.message}
                        </div>
                        {result.ticket && (
                            <div className="mt-4 space-y-1 font-mono2 text-xs opacity-90">
                                <div data-testid="checkin-result-code">{result.ticket.code}</div>
                                <div>{result.ticket.attendee_name} · {result.ticket.category}</div>
                                <div>{result.ticket.event_name}</div>
                            </div>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
}
